import { reactive, computed, ref } from 'vue';
import { updateSearchParamsWithRouter } from '@/Utils/FiltersUtility';
import { convertSearchParamsToObject } from '@/Utils/UrlUtility.js';
import WebStorage from '@/Utils/WebStorage.js';
import { useUserContextStore } from '@/Stores/Web/userContextStore';

import { useApiFetch } from '@/Composables/useApiFetch.js';
import { useFiltersLabelMapper } from '@/Composables/Filters/useFiltersLabelMapper.js';

export function useFilters(options = {}) {
    const { userId } = useUserContextStore();
    const { execute } = useApiFetch();
    const {
        buildDefaultFilterParams,
        buildCurrentFilter,
        buildFilterList,
        buildSortList,
        removeSelectedFilter,
        apiLoadMethod,
        storageKey,
        resources = null,
        sortEnum = null,
        defaultPageSize = 10,
    } = options;
    const { getSortDisplayName, mapFilters } = useFiltersLabelMapper(resources);
    const storage = new WebStorage(storageKey);
    const router = ref(null);

    // Common reactive state
    const state = reactive({
        searchParams: buildDefaultFilterParams(),
        totalItemCount: 0,
        totalPageCount: 0,
        pageIndex: 0,
        pageSize: defaultPageSize,
        pagedItems: [],
        isProcessing: false,
        isLoadingMore: false,
        isInitialized: false,
        responseData: {},
        selectedFilters: {},
        errorHandler: (error) => {
            throw error;
        },
    });

    // Common computed values
    const hasItems = computed(() => state.pagedItems.length > 0);
    const canLoadMoreRows = computed(() => state.pagedItems.length < state.totalItemCount && !state.isLoadingMore);
    const sortDisplayName = computed(() => {
        return getSortDisplayName(state.searchParams.sortType, sortEnum);
    });

    // State properties as computed
    const filterSchema = computed(() => {
        try {
            return buildFilterList ? buildFilterList() : [];
        }
        catch (error) {
            return [];
        }
    });
    const sortSchema = computed(() => {
        try {
            return buildSortList ? buildSortList() : [];
        }
        catch (error) {
            return [];
        }
    });
    const totalItemCount = computed(() => state.totalItemCount);
    const searchParams = computed(() => state.searchParams);
    const pagedItems = computed(() => state.pagedItems);
    const isProcessing = computed(() => state.isProcessing);
    const isLoadingMore = computed(() => state.isLoadingMore);
    const isInitialized = computed(() => state.isInitialized);
    const selectedFilters = computed(() => state.selectedFilters);

    // Get initial params with router/storage priority
    const getInitialSearchParams = () => {
        const defaultParams = buildDefaultFilterParams();

        // Check router query params first
        const routerQuery = router.value?.currentRoute?.query || {};
        const hasRouterParams = Object.keys(routerQuery).length > 0;
        if (hasRouterParams) {
            const urlSearchParams = new URLSearchParams(routerQuery);
            const formattedRouterQuery = buildDefaultFilterParams(convertSearchParamsToObject(urlSearchParams));
            return { ...defaultParams, ...formattedRouterQuery };
        }
        else {
            // Fallback to storage
            const savedParams = storage.get();
            if (savedParams?.search) {
                const urlSearchParams = new URLSearchParams(savedParams.search);
                const storageParams = buildDefaultFilterParams(convertSearchParamsToObject(urlSearchParams));
                return { ...defaultParams, ...storageParams };
            }
            return defaultParams;
        }
    };

    // Common data loading
    const loadData = async (params = {}, loadMore = false, skipCache = false) => {
        try {
            state.isProcessing = !loadMore;
            state.isLoadingMore = loadMore;

            params.pageSize = state.pageSize;
            if (loadMore) {
                params.pageIndex = state.pageIndex + 1;
            }
            return await execute(
                (signal) => apiLoadMethod({ ...params, signal }, loadMore),
                {
                    cache: {
                        enabled: !loadMore, // Don't cache "load more" requests
                        key: `${userId}:${JSON.stringify(params)}`,
                        skipCache: loadMore || skipCache,
                    },
                    onSuccess: (response) => {
                    // Handle success - update pagination, etc.
                        if (response) {
                            handleResponseApi(response, loadMore);
                        }
                    },
                    onError: (error) => {
                        state.isProcessing = false;
                        state.isLoadingMore = false;
                        state.errorHandler(error);
                    },
                },
            );
        }
        finally {
            state.isProcessing = false;
            state.isLoadingMore = false;
        }

    };

    const handleResponseApi = ({ data }, loadMore) => {
        state.totalItemCount = data.totalItemCount || 0;
        state.totalPageCount = data.totalPageCount || 0;
        state.pageIndex = data.pageIndex || 0;
        state.pageSize = data.pageSize || state.pageSize;

        if (data.pagedItems) {
            if (loadMore) {
                state.pagedItems = [...state.pagedItems, ...data.pagedItems];
            }
            else {
                state.pagedItems = data.pagedItems;
            }
        }

        state.responseData = data;

        // Update search params but preserve current sort if not in response
        if (data.params) {
            const currentSortBy = state.searchParams.sortType;
            const newParams = buildDefaultFilterParams(data.params);

            if (!Object.prototype.hasOwnProperty.call(data.params, 'sortType') && currentSortBy !== undefined) {
                newParams.sortType = currentSortBy;
            }
            state.searchParams = newParams;
            state.selectedFilters = { ...newParams };
        }

        // Update URL and storage
        updateSearchParamsWithRouter(data.searchResultQuery, router.value);
        storage.set({ search: data.searchResultQuery });
    };

    // Common filter operations
    const resetData = () => {
        state.pagedItems = [];
        state.totalItemCount = 0;
        state.totalPageCount = 0;
        state.pageIndex = 0;
    };

    const onChange = async (filterKey, value) => {
        resetData();
        state.searchParams[filterKey] = value;
        await loadData(state.searchParams);
    };

    const applyFilters = async (filters) => {
        resetData();
        state.searchParams = { ...filters };
        state.selectedFilters = { ...filters };
        await loadData(filters);
    };

    const deleteFilter = async (filter) => {
        resetData();
        const updatedParams = removeSelectedFilter(filter, state.searchParams);
        state.searchParams = { ...updatedParams };
        state.selectedFilters = { ...updatedParams };
        await loadData(updatedParams);
    };

    const applySorting = async (sortType) => {
        resetData();
        state.searchParams.sortType = sortType;
        state.selectedFilters.sortType = sortType;
        await loadData(state.searchParams);
    };

    const loadMoreRows = async () => {
        if (!canLoadMoreRows.value) return;
        await loadData({ ...state.searchParams, pageIndex: state.pageIndex + 1 }, true);
    };

    const getSelectedFilters = () => {
        const filters = buildCurrentFilter(state.searchParams);
        return resources ? mapFilters(filters) : filters;
    };

    const resetFilters = (hiddenFilters) => {
        const preservedFilters = hiddenFilters.reduce((acc, filter) => {
            acc[filter] = state.selectedFilters[filter];
            return acc;
        }, {});

        const initialParams = {
            sortType: state.selectedFilters.sortType,
            ...preservedFilters,
        };

        state.selectedFilters = buildDefaultFilterParams(initialParams);
    };

    const setSortBy = (sortType) => {
        state.searchParams.sortType = sortType;
    };

    const initialize = async (newRouter, errorHandler, skipCache = false) => {
        if (errorHandler) {
            state.errorHandler = errorHandler;
        }
        if (newRouter) {
            router.value = newRouter;
        }
        const initialParams = getInitialSearchParams();
        await loadData(initialParams, false, skipCache);
        state.isInitialized = true;
    };

    return {
        state,

        hasItems,
        canLoadMoreRows,
        filterSchema,
        sortSchema,
        totalItemCount,
        searchParams,
        pagedItems,
        isProcessing,
        isLoadingMore,
        isInitialized,
        sortDisplayName,
        selectedFilters,

        // Getters
        getSelectedFilters,

        // Methods
        onChange,
        applyFilters,
        deleteFilter,
        applySorting,
        loadMoreRows,
        resetFilters,
        setSortBy,
        initialize,
        loadData,
    };
}